import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Skeleton,
  styled,
  Typography,
} from '@mui/material';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import MuiAccordionSummary, { accordionSummaryClasses, AccordionSummaryProps } from '@mui/material/AccordionSummary';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronDownIcon, PlusIcon } from 'lucide-react';
import * as React from 'react';
import { MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useNavigate, useParams } from 'react-router';
import disparities_search_icon from '../../../assets/images/disparities_search.svg';
import { useBuilderBoxApi } from '../../../services/api/useBuilderBox.api.ts';
import { useCaseApi } from '../../../services/api/useCase.api.ts';
import { useDisparityApi } from '../../../services/api/useDisparity.api.ts';
import { ApiResponse } from '../../../types/ApiResponse.type.ts';
import { Case } from '../../../types/Case/Case.type.ts';
import { Disparity } from '../../../types/Disparity/Disparity.type.ts';
import { DisparityCalculationStatus } from '../../../types/enums/DisparityCalculationStatus.enum.ts';
import { Resource } from '../../../types/Resources/Resource.type.ts';
import { BuilderBox } from '../../../types/SimilarlySituated/BuilderBox.type.ts';
import NewCaseSkeleton from '../NewCaseSkeleton.tsx';
import BuildSelectionItem from './BuildSelectionItem.tsx';
import DisparitySelectionItem from './DisparitySelectionItem.tsx';
import NewDisparitySearch from './NewDisparitySearch.tsx';
import ResourceSelectionItem from './ResourceSelectionItem.tsx';

const StyledDisparityPanel = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  width: '540px',
  overflow: 'hidden',
  background: '#fff',
  borderRadius: '8px',
  boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)',
  border: '1px solid #cbd5e0',
});

const StyledItemCountBadge = styled('span')(({ theme }) => ({
  fontSize: '12px',
  color: '#fff',
  backgroundColor: theme.palette.primary.main,
  borderRadius: '12px',
  padding: '2px 8px',
  fontWeight: 600,
}));

const Accordion = styled((props: AccordionProps) => <MuiAccordion disableGutters elevation={0} square {...props} />)(
  ({ theme }) => ({
    border: 0,
    borderTop: `1px solid ${theme.palette.divider}`,
    borderBottom: `1px solid ${theme.palette.divider}`,
    boxShadow: 'none',
    borderRadius: '0 !important',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minHeight: 0,
    '&:not(:last-child)': {
      borderBottom: 0,
    },
    '&::before': {
      display: 'none',
    },

    '& .MuiAccordion-region': {
      display: 'flex',
      flex: '1 1 auto',
      minHeight: 0,
    },
    '& .MuiCollapse-root': {
      display: 'flex',
      flex: '1 1 auto',
      minHeight: 0,
      overflow: 'hidden',
    },
    '& .MuiCollapse-wrapper': {
      display: 'flex',
      flex: '1 1 auto',
      minHeight: 0,
    },
    '& .MuiCollapse-wrapperInner': {
      display: 'flex',
      flex: '1 1 auto',
      minHeight: 0,
      width: '100%',
    },
  }),
);

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary expandIcon={<ChevronDownIcon size={26} color='#20567E' />} {...props} />
))(({ theme }) => ({
  backgroundColor: 'rgba(0, 0, 0, .03)',
  flex: '0 0 auto',
  [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]: {
    transform: 'rotate(180deg)',
  },
  [`& .${accordionSummaryClasses.content}`]: {
    marginLeft: theme.spacing(1),
  },
  ...theme.applyStyles('dark', {
    backgroundColor: 'rgba(255, 255, 255, .05)',
  }),
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: '1px solid rgba(0, 0, 0, .125)',
  flex: '1 1 auto',
  minHeight: 0,
  overflowY: 'auto',
  overscrollBehavior: 'contain',
}));

interface SelectionPanelProps {
  onDisparitySelect: (disparityId: number) => void;
  onResourceSelect: (resourceId: number) => void;
  selectedDisparityId: number | null;
  selectedResourceId: number | null;
  caseData?: Case;
  onDisparityDelete: () => void;
  isSsbEnabled?: boolean;
}

function SelectionPanel({
  onDisparitySelect,
  onResourceSelect,
  selectedDisparityId,
  selectedResourceId,
  caseData,
  onDisparityDelete,
  isSsbEnabled = false,
}: SelectionPanelProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const disparitiesDetailsRef = useRef<HTMLDivElement | null>(null);
  const resourcesDetailsRef = useRef<HTMLDivElement | null>(null);
  const buildsDetailsRef = useRef<HTMLDivElement | null>(null);
  const [disparitiesInViewRoot, setDisparitiesInViewRoot] = useState<HTMLDivElement | null>(null);
  const [resourcesInViewRoot, setResourcesInViewRoot] = useState<HTMLDivElement | null>(null);
  const [buildsInViewRoot, setBuildsInViewRoot] = useState<HTMLDivElement | null>(null);
  const disparityRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const prevInvalidDisparityIdsRef = useRef<Set<number>>(new Set());

  const handleDisparitiesDetailsRef = useCallback((node: HTMLDivElement | null) => {
    disparitiesDetailsRef.current = node;
    setDisparitiesInViewRoot(node);
  }, []);

  const handleResourcesDetailsRef = useCallback((node: HTMLDivElement | null) => {
    resourcesDetailsRef.current = node;
    setResourcesInViewRoot(node);
  }, []);

  const handleBuildsDetailsRef = useCallback((node: HTMLDivElement | null) => {
    buildsDetailsRef.current = node;
    setBuildsInViewRoot(node);
  }, []);

  const { ref: disparityScrollRef, inView: inViewDisparities } = useInView({
    root: disparitiesInViewRoot ?? undefined,
  });
  const { ref: resourcesScrollRef, inView: inViewResources } = useInView({
    root: resourcesInViewRoot ?? undefined,
  });
  const { ref: buildsScrollRef, inView: inViewBuilds } = useInView({
    root: buildsInViewRoot ?? undefined,
  });
  const { caseId } = useParams<{ caseId?: string }>();
  const { getDisparitiesByCaseId, deleteDisparities, getCaseResources, updateCaseResource, getBuilderBoxesByCaseId } =
    useCaseApi();
  const { updateDisparity } = useDisparityApi();
  const { updateBuilderBox } = useBuilderBoxApi();

  const [newDisparityDialogVisible, setNewDisparityDialogVisible] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [open, setOpen] = useState(false);
  const [updatingDisparityId, setUpdatingDisparityId] = useState<number | null>(null);
  const [updatingResourceId, setUpdatingResourceId] = useState<number | null>(null);
  const [updatingBuildId, setUpdatingBuildId] = useState<number | null>(null);
  const [expanded, setExpanded] = React.useState<string | false>('disparities');

  const handleAccordionChange = (panel: string) => (_event: React.SyntheticEvent, newExpanded: boolean) => {
    setExpanded(newExpanded ? panel : false);
    onDisparityDelete();
  };

  const handleClearDisparitiesDialogToggle = () => {
    setOpen(!open);
  };

  const handleDisparitiesDelete = () => {
    deleteDisparitiesMutation();
    setOpen(false);
  };

  const handleDisparityDialogToggle = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
    setNewDisparityDialogVisible(!newDisparityDialogVisible);
  };

  const handleCloseNewDisparityDialog = () => {
    setNewDisparityDialogVisible(false);
    setAnchorEl(null);
  };

  const {
    data: caseDisparities,
    fetchNextPage: fetchNextDisparities,
    hasNextPage: hasNextDisparities,
    isFetchingNextPage: isFetchingNextDisparities,
    isLoading: caseDisparitiesFetching,
  } = useInfiniteQuery({
    queryKey: ['caseDisparities', caseId],
    queryFn: async ({ pageParam = 1 }) => {
      if (!caseId) {
        throw new Error();
      }
      return await getDisparitiesByCaseId(Number(caseId), {
        page: pageParam,
        per_page: 50,
        sort: 'pinned:desc,score:desc',
      });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: ApiResponse<Disparity[]>) => {
      if ((lastPage?.meta.pagination?.page ?? 0) < (lastPage?.meta.pagination?.pages ?? 0)) {
        return (lastPage?.meta.pagination?.page ?? 0) + 1;
      }
      return undefined;
    },
    enabled: !!caseId,
    refetchInterval: (query) => {
      const disparities = query.state.data?.pages?.flatMap((p) => p.data) ?? [];

      const hasIncomplete = disparities.some(
        (d) =>
          d.calculation_status?.id !== DisparityCalculationStatus.COMPLETED ||
          d.score === null ||
          isNaN(Number(d.score)) ||
          (d.score !== undefined && d.score <= 0),
      );

      return hasIncomplete ? 5000 : false;
    },
  });

  const {
    data: caseResources,
    fetchNextPage: fetchNextResources,
    hasNextPage: hasNextResources,
    isFetchingNextPage: isFetchingNextResources,
    isLoading: caseResourcesFetching,
  } = useInfiniteQuery({
    queryKey: ['caseResources', caseId],
    queryFn: async ({ pageParam = 1 }) => {
      if (!caseId) {
        throw new Error();
      }
      return await getCaseResources(Number(caseId), { page: pageParam, per_page: 50, sort: 'pinned:desc' });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: ApiResponse<Resource[]>) => {
      if ((lastPage?.meta.pagination?.page ?? 0) < (lastPage?.meta.pagination?.pages ?? 0)) {
        return (lastPage?.meta.pagination?.page ?? 0) + 1;
      }
      return undefined;
    },
    enabled: !!caseId,
  });

  const {
    data: caseBuilds,
    fetchNextPage: fetchNextBuilds,
    hasNextPage: hasNextBuilds,
    isFetchingNextPage: isFetchingNextBuilds,
    isLoading: caseBuildsFetching,
  } = useInfiniteQuery({
    queryKey: ['caseBuilderBoxes', caseId, 'selection-panel'],
    queryFn: async ({ pageParam = 1 }) => {
      if (!caseId) {
        throw new Error();
      }
      return await getBuilderBoxesByCaseId(Number(caseId), {
        page: pageParam,
        per_page: 50,
        sort: 'pinned:desc,updated_at:desc',
      });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: ApiResponse<BuilderBox[]>) => {
      if ((lastPage?.meta.pagination?.page ?? 0) < (lastPage?.meta.pagination?.pages ?? 0)) {
        return (lastPage?.meta.pagination?.page ?? 0) + 1;
      }
      return undefined;
    },
    enabled: !!caseId && isSsbEnabled,
  });

  const { mutate: updateDisparityMutation } = useMutation({
    mutationFn: ({ disparityId, pinned }: { disparityId: number; pinned: boolean }) =>
      updateDisparity(disparityId, { pinned }),
    onMutate: async ({ disparityId, pinned }) => {
      setUpdatingDisparityId(disparityId);

      await queryClient.cancelQueries({ queryKey: ['caseDisparities', caseId] });

      const previousDisparities = queryClient.getQueryData(['caseDisparities', caseId]);

      queryClient.setQueryData<{ pages: ApiResponse<Disparity[]>[]; pageParams: number[] }>(
        ['caseDisparities', caseId],
        (old) => {
          if (!old) return old;

          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              data: page.data.map((disparity: Disparity) =>
                disparity.id === disparityId ? { ...disparity, pinned } : disparity,
              ),
            })),
          };
        },
      );

      return { previousDisparities };
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['caseDisparities'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['caseExportData'], exact: false });
      setUpdatingDisparityId(null);
    },
    onError: (_err, _variables, context) => {
      if (context?.previousDisparities) {
        queryClient.setQueryData(['caseDisparities', caseId], context.previousDisparities);
      }
      setUpdatingDisparityId(null);
    },
  });

  const { mutate: updateResourceMutation } = useMutation({
    mutationFn: ({ caseId, resourceId, pinned }: { caseId: number; resourceId: number; pinned: boolean }) =>
      updateCaseResource(caseId, resourceId, { pinned }),
    onMutate: async ({ caseId, resourceId, pinned }) => {
      setUpdatingResourceId(resourceId);

      await queryClient.cancelQueries({ queryKey: ['caseResources', caseId, resourceId] });

      const previousResources = queryClient.getQueryData(['caseResources', caseId, resourceId]);

      queryClient.setQueryData<{ pages: ApiResponse<Resource[]>[]; pageParams: number[] }>(
        ['caseResources', caseId, resourceId],
        (old) => {
          if (!old) return old;

          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              data: page.data.map((resource: Resource) =>
                resource.id === resourceId ? { ...resource, pinned } : resource,
              ),
            })),
          };
        },
      );

      return { previousResources };
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['caseResources'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['caseExportData'], exact: false });
      setUpdatingResourceId(null);
    },
    onError: (_err, _variables, context) => {
      if (context?.previousResources) {
        queryClient.setQueryData(
          ['caseResources', _variables.caseId, _variables.resourceId],
          context.previousResources,
        );
      }
      setUpdatingResourceId(null);
    },
  });

  const { mutate: updateBuildMutation } = useMutation({
    mutationFn: ({ buildId, pinned }: { buildId: number; pinned: boolean }) => updateBuilderBox(buildId, { pinned }),
    onMutate: async ({ buildId, pinned }) => {
      setUpdatingBuildId(buildId);

      const buildsQueryKey = ['caseBuilderBoxes', caseId, 'selection-panel'];
      await queryClient.cancelQueries({ queryKey: buildsQueryKey });

      const previousBuilds = queryClient.getQueryData(buildsQueryKey);

      queryClient.setQueryData<{ pages: ApiResponse<BuilderBox[]>[]; pageParams: number[] }>(buildsQueryKey, (old) => {
        if (!old) return old;

        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            data: page.data.map((build: BuilderBox) => (build.id === buildId ? { ...build, pinned } : build)),
          })),
        };
      });

      return { previousBuilds };
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['caseBuilderBoxes'], exact: false });
      setUpdatingBuildId(null);
    },
    onError: (_err, _variables, context) => {
      if (context?.previousBuilds) {
        queryClient.setQueryData(['caseBuilderBoxes', caseId, 'selection-panel'], context.previousBuilds);
      }
      setUpdatingBuildId(null);
    },
  });

  const { mutate: deleteDisparitiesMutation } = useMutation({
    mutationKey: ['deleteDisparities', caseId],
    mutationFn: () => deleteDisparities(Number(caseId), { ids: [] }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['caseDisparities'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['caseResources'], exact: false });
      queryClient.invalidateQueries({ queryKey: ['caseExportData'], exact: false });
      onDisparityDelete();
    },
  });

  useEffect(() => {
    if (inViewDisparities && fetchNextDisparities && hasNextDisparities && !isFetchingNextDisparities) {
      fetchNextDisparities();
    }
  }, [inViewDisparities, fetchNextDisparities, isFetchingNextDisparities, hasNextDisparities]);

  useEffect(() => {
    if (inViewResources && fetchNextResources && hasNextResources && !isFetchingNextResources) {
      fetchNextResources();
    }
  }, [inViewResources, fetchNextResources, isFetchingNextResources, hasNextResources]);

  useEffect(() => {
    if (inViewBuilds && fetchNextBuilds && hasNextBuilds && !isFetchingNextBuilds) {
      fetchNextBuilds();
    }
  }, [inViewBuilds, fetchNextBuilds, isFetchingNextBuilds, hasNextBuilds]);

  const disparities = caseDisparities?.pages?.flatMap((page) => page.data) ?? [];
  const resources = caseResources?.pages?.flatMap((page) => page.data) ?? [];
  const builds = caseBuilds?.pages?.flatMap((page) => page.data) ?? [];

  // opening a build hands off to the builder surface with that box loaded
  const handleBuildSelect = (build: BuilderBox) => {
    navigate(`/similarly-situated-builder/${caseId}`, { state: { openBuildId: build.id } });
  };

  const validDisparities = disparities.filter(
    (d) =>
      d.calculation_status?.id === DisparityCalculationStatus.COMPLETED &&
      d.score != null &&
      !isNaN(Number(d.score)) &&
      d.score > 0,
  );

  const invalidDisparities = disparities.filter(
    (d) =>
      !(
        d.calculation_status?.id === DisparityCalculationStatus.COMPLETED &&
        d.score != null &&
        !isNaN(Number(d.score)) &&
        d.score > 0
      ),
  );

  const pinnedDisparitiesCount = disparities.filter((d) => d.pinned).length;
  const pinnedResourcesCount = resources.filter((r) => r.pinned).length;

  const [prevSelectedDisparityId, setPrevSelectedDisparityId] = useState(selectedDisparityId);
  if (selectedDisparityId && selectedDisparityId !== prevSelectedDisparityId) {
    setExpanded('disparities');
  }
  if (selectedDisparityId !== prevSelectedDisparityId) {
    setPrevSelectedDisparityId(selectedDisparityId);
  }

  useEffect(() => {
    const currentInvalidIds = new Set(invalidDisparities.map((d) => d.id));
    const prevInvalidDisparityIds = prevInvalidDisparityIdsRef.current;

    if (selectedDisparityId) {
      const isDisparityValid =
        prevInvalidDisparityIds.has(selectedDisparityId) && !currentInvalidIds.has(selectedDisparityId);

      const scrollToDisparity = () => {
        const element = disparityRefs.current.get(selectedDisparityId);
        if (element && disparitiesDetailsRef.current) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      };

      if (isDisparityValid) {
        setTimeout(scrollToDisparity, 100);
      } else {
        scrollToDisparity();
      }
    }

    prevInvalidDisparityIdsRef.current = currentInvalidIds;
  }, [selectedDisparityId, invalidDisparities, disparities?.length]);

  if (!caseId) {
    return (
      <StyledDisparityPanel sx={{ padding: '16px' }}>
        <NewCaseSkeleton />
      </StyledDisparityPanel>
    );
  }

  return (
    <StyledDisparityPanel id='tour-disparity-panel'>
      <Box
        sx={{
          margin: '16px',
          border: '1px solid #dbdbdb',
          backgroundColor: '#f5f5f5',
          borderRadius: '8px',
          paddingY: '18px',
          paddingX: '24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '18px',
        }}
      >
        <img src={disparities_search_icon} alt='' />
        <Button
          onClick={handleDisparityDialogToggle}
          variant='contained'
          fullWidth
          disableElevation
          sx={{
            fontWeight: 600,
            textTransform: 'none',
            paddingY: '10px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
          id='tour-new-disparity-search-button'
        >
          <PlusIcon size={16} />
          New Disparity Search
        </Button>
        <NewDisparitySearch
          open={newDisparityDialogVisible}
          close={handleCloseNewDisparityDialog}
          anchorEl={anchorEl}
          caseData={caseData}
          onDisparitySelect={onDisparitySelect}
        />
      </Box>
      <Dialog
        fullWidth
        maxWidth='xs'
        open={open}
        onClose={handleClearDisparitiesDialogToggle}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
      >
        <DialogTitle sx={{ fontSize: '16px' }}>Delete all disparities</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '14px' }}>
            Are you sure you want to delete all disparities for this case? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClearDisparitiesDialogToggle}>Cancel</Button>
          <Button onClick={handleDisparitiesDelete} color='error'>
            Delete all
          </Button>
        </DialogActions>
      </Dialog>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0,
          flex: 1,
          minHeight: 0,
          flexWrap: 'nowrap',
          overflow: 'hidden',
        }}
      >
        <Accordion
          expanded={expanded === 'disparities'}
          onChange={handleAccordionChange('disparities')}
          sx={{ flex: expanded === 'disparities' ? '1 1 auto' : '0 0 auto' }}
        >
          <AccordionSummary aria-controls='disparities-content' id='disparities-header' component='div'>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Typography component='span' sx={{ fontSize: '16px', fontWeight: 600 }}>
                  Disparities
                </Typography>
                {validDisparities.length > 0 && pinnedDisparitiesCount > 0 && (
                  <StyledItemCountBadge>{pinnedDisparitiesCount}</StyledItemCountBadge>
                )}
              </Box>
              {disparities.length > 0 && (
                <Button
                  color='accentBlue'
                  variant='text'
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClearDisparitiesDialogToggle();
                  }}
                  sx={{ mr: 1 }}
                >
                  Clear all
                </Button>
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails ref={handleDisparitiesDetailsRef}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
              }}
            >
              {caseDisparitiesFetching ? (
                <>
                  <Skeleton variant='rounded' height={124} />
                  <Skeleton variant='rounded' height={124} />
                  <Skeleton variant='rounded' height={124} />
                </>
              ) : (
                <>
                  {disparities.length > 0 ? (
                    <>
                      {validDisparities.map((caseDisparity) => (
                        <Box
                          ref={(el: HTMLDivElement) => {
                            if (el) {
                              disparityRefs.current.set(caseDisparity.id, el);
                            } else {
                              disparityRefs.current.delete(caseDisparity.id);
                            }
                          }}
                          key={caseDisparity.id}
                        >
                          <DisparitySelectionItem
                            caseDisparity={caseDisparity}
                            selectedDisparityId={selectedDisparityId}
                            onDisparitySelect={onDisparitySelect}
                            updateDisparityMutation={updateDisparityMutation}
                            isUpdatingDisparity={updatingDisparityId === caseDisparity.id}
                          />
                        </Box>
                      ))}

                      {validDisparities.length === 0 && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            border: '1px solid #e2e8f0',
                            borderRadius: '6px',
                            paddingX: '20px',
                            paddingY: '28px',
                          }}
                        >
                          <Typography variant='body2'>No valid disparities for the selected case.</Typography>
                        </Box>
                      )}
                      {invalidDisparities.length > 0 && <Divider sx={{ marginY: 4 }} />}

                      {invalidDisparities.map((caseDisparity) => (
                        <Box
                          ref={(el: HTMLDivElement) => {
                            if (el) {
                              disparityRefs.current.set(caseDisparity.id, el);
                            } else {
                              disparityRefs.current.delete(caseDisparity.id);
                            }
                          }}
                          key={caseDisparity.id}
                        >
                          <DisparitySelectionItem
                            caseDisparity={caseDisparity}
                            selectedDisparityId={selectedDisparityId}
                            onDisparitySelect={onDisparitySelect}
                            updateDisparityMutation={updateDisparityMutation}
                            isUpdatingDisparity={updatingDisparityId === caseDisparity.id}
                            hidden
                          />
                        </Box>
                      ))}
                    </>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        paddingX: '20px',
                        paddingY: '28px',
                      }}
                    >
                      <Typography variant='body2'>No disparities for the selected case.</Typography>
                    </Box>
                  )}
                  {hasNextDisparities && (
                    <div ref={disparityScrollRef} style={{ height: '1px', width: '100%' }}>
                      &nbsp;
                    </div>
                  )}
                  {isFetchingNextDisparities && (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
                      <Typography
                        variant='caption'
                        sx={{
                          color: '#a0aec0',
                        }}
                      >
                        Loading more disparities...
                      </Typography>
                    </div>
                  )}
                </>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={expanded === 'resources'}
          onChange={handleAccordionChange('resources')}
          sx={{ flex: expanded === 'resources' ? '1 1 auto' : '0 0 auto' }}
        >
          <AccordionSummary aria-controls='resources-content' id='resources-header'>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Typography component='span' sx={{ fontSize: '16px', fontWeight: 600 }}>
                Resources
              </Typography>
              {resources.length > 0 && pinnedResourcesCount > 0 && (
                <StyledItemCountBadge>{pinnedResourcesCount}</StyledItemCountBadge>
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails ref={handleResourcesDetailsRef}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
              }}
            >
              {caseResourcesFetching ? (
                <>
                  <Skeleton variant='rounded' height={124} />
                  <Skeleton variant='rounded' height={124} />
                  <Skeleton variant='rounded' height={124} />
                </>
              ) : (
                <>
                  {resources.length > 0 ? (
                    <>
                      {resources.map((resource) => (
                        <ResourceSelectionItem
                          key={resource.id}
                          caseResource={resource}
                          selectedResourceId={selectedResourceId ?? null}
                          onResourceSelect={onResourceSelect}
                          updateResourceMutation={updateResourceMutation}
                          isUpdatingResource={updatingResourceId === resource.id}
                        />
                      ))}
                      {hasNextResources && (
                        <div ref={resourcesScrollRef} style={{ height: '1px', width: '100%' }}>
                          &nbsp;
                        </div>
                      )}
                      {isFetchingNextResources && (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
                          <Typography
                            variant='caption'
                            sx={{
                              color: '#a0aec0',
                            }}
                          >
                            Loading more resources...
                          </Typography>
                        </div>
                      )}
                    </>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        paddingX: '20px',
                        paddingY: '28px',
                      }}
                    >
                      <Typography variant='body2'>No resources available for this case.</Typography>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>

        {isSsbEnabled && (
          <Accordion
            expanded={expanded === 'builds'}
            onChange={handleAccordionChange('builds')}
            sx={{ flex: expanded === 'builds' ? '1 1 auto' : '0 0 auto' }}
          >
            <AccordionSummary aria-controls='builds-content' id='builds-header'>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Typography component='span' sx={{ fontSize: '16px', fontWeight: 600 }}>
                  Similarly Situated Cases
                </Typography>
                {builds.length > 0 && <StyledItemCountBadge>{builds.length}</StyledItemCountBadge>}
              </Box>
            </AccordionSummary>
            <AccordionDetails ref={handleBuildsDetailsRef}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                }}
              >
                {caseBuildsFetching ? (
                  <>
                    <Skeleton variant='rounded' height={124} />
                    <Skeleton variant='rounded' height={124} />
                    <Skeleton variant='rounded' height={124} />
                  </>
                ) : (
                  <>
                    {builds.length > 0 ? (
                      <>
                        {builds.map((build) => (
                          <BuildSelectionItem
                            key={build.id}
                            build={build}
                            onBuildSelect={handleBuildSelect}
                            updateBuildMutation={updateBuildMutation}
                            isUpdatingBuild={updatingBuildId === build.id}
                          />
                        ))}
                        {hasNextBuilds && (
                          <div ref={buildsScrollRef} style={{ height: '1px', width: '100%' }}>
                            &nbsp;
                          </div>
                        )}
                        {isFetchingNextBuilds && (
                          <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
                            <Typography
                              variant='caption'
                              sx={{
                                color: '#a0aec0',
                              }}
                            >
                              Loading more saved boxes...
                            </Typography>
                          </div>
                        )}
                      </>
                    ) : (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          border: '1px solid #e2e8f0',
                          borderRadius: '6px',
                          paddingX: '20px',
                          paddingY: '28px',
                        }}
                      >
                        <Typography variant='body2'>No saved boxes for this case.</Typography>
                      </Box>
                    )}
                  </>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        )}
      </Box>
    </StyledDisparityPanel>
  );
}

export default SelectionPanel;
